import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  AdminPortalApplications,
  PortalNavTab,
  BulkSelectOptions,
  FlexDiv,
  TextMessage,
  OrangeButton,
  OrangeButtonSimpleText
} from 'src/components/portal/default'
import PillButton from 'src/components/basic/PillButton'
import StandardSelect from 'src/components/basic/StandardSelect'
import {
  AppStatusFilterOptions,
  NavTabOptions,
  ApplicationTypeFilterOptions,
  ApplicationListResponseData,
  RenewalsListResponseData,
  ExpiringPoliciesListData,
  NewContactSelection,
  FirmDetailsContactData
} from 'src/dtos/portal'
import {
  RenewalData
} from 'src/dtos/renewalData'
import {
  dropdownDatesFilterOptions,
  dropdownFilterOptions,
  statusValuesToFetchApplications,
  statusToCapitalizedName
} from 'src/constants/portal'
import BulkSelect from 'src/components/portal/default/BulkSelect'
import AppListRow from 'src/components/portal/default/AppListRow'
import RenewalListRow from 'src/components/portal/default/RenewalListRow'
import ExpiringPoliciesListRow from 'src/components/portal/default/ExpiringPoliciesListRow'
import PortalRightDrawer from 'src/components/basic/PortalRightDrawer'
import DrawerHeader from 'src/components/portal/default/AppListDrawerHeader'
import AppListDrawerContent from 'src/components/portal/default/AppListDrawerContent'
import ExistingContactSelect from 'src/components/portal/default/ExistingContactSelect'
import CircleActionButton from 'src/components/basic/CircleActionButton'
import Pagination from 'src/components/basic/Pagination'
import { useToast } from 'src/hooks/useToast'
import { ISIExistingContactData } from 'src/dtos/portal'
import { subDays, subMonths, addDays } from 'date-fns'
import { api } from 'src/services/apiClient'
import { FiSend } from 'react-icons/fi'
import { MdClose, MdDeleteOutline, MdOutlineDownload, MdRemoveRedEye } from 'react-icons/md'
import { TbMail } from 'react-icons/tb'
import loadingImg from 'src/assets/loader-green.svg'
import Modal from 'react-modal'
import { ScreenshotLoadingContainer } from './styles'

const { useScreenshot, createFileName } = require('use-react-screenshot'); 

type SortingOptions = typeof dropdownFilterOptions[number]

type DateRangeOptions = typeof dropdownDatesFilterOptions[number]

const Applications = () => {
  const [selectedTab, setSelectedTab] = useState<NavTabOptions>('NEW')
  const [applicationStatusFilter, setApplicationStatusFilter] =
    useState<AppStatusFilterOptions>('COMPLETED')
  const [applicationTypeBulkFilter, setApplicationTypeBulkFilter] =
    useState<ApplicationTypeFilterOptions | undefined>(undefined)
  const [sortingOption, setSortingOption] = useState<SortingOptions>('Newest to Oldest')
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOptions>('Last 12 months')
  const [isLoadingApplications, setIsLoadingApplications] = useState(false)
  const [checkedApplications, setCheckedApplications] = useState<number[]>([])
  const [openApplicationId, setOpenApplicationId] = useState(0)
  const [drawerExpiringAplicationType, setDrawerExpiringAplicationType] = useState('NEW')
  const [currentPage, setCurrentPage] = useState(1)
  const [drawerHidden, setDrawerHidden] = useState(false)
  const [drawerApplication, setDrawerApplication] = useState<RenewalData | undefined>(undefined)
  const [primaryContactData, setPrimaryContactData] = useState<FirmDetailsContactData | undefined>(undefined)
  const [openSelectContactForSubmit, setopenSelectContactForSubmit] = useState<ISIExistingContactData[]>([])
  const [applicationsList, setApplicationsList] = useState<ApplicationListResponseData>({
    currentPage: 0,
    totalCount: 0,
    totalPages: 0,
    applications: []
  })
  const [renewalsList, setRenewalsList] = useState<RenewalsListResponseData>({
    currentPage: 0,
    totalCount: 0,
    totalPages: 0,
    renewals: []
  })
  const [expiringPoliciesList, setExpiringPoliciesList] = useState<ExpiringPoliciesListData[]>([])
  const [rejectAppModalOpen, setRejectAppModalOpen] = useState(false)
  const [rejectModalBtnsDisabled, setRejectModalBtnsDisabled] = useState(false)
  const [impersonateModalBtnsDisabled, setImpersonateModalBtnsDisabled] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)

  const { callToast } = useToast()

  const appSummaryRef = useRef<any>(null);
  const [image, takeScreenShot] = useScreenshot({
    type: "image/jpeg",
    quality: 1.0
  });
  const [isLoadingScreenshot, setIsLoadingScreenshot] = useState(false);


  const expiringPoliciesToShow = useMemo(() => {
    return expiringPoliciesList.slice((currentPage * 5 - 5), (currentPage * 5))
  }, [currentPage, expiringPoliciesList])

  useEffect(() => {
    const mainPortal = document.getElementById('admin-main-portal-container')

    if(mainPortal) {
      if(isSubmitting === true) {
        mainPortal.classList.add('no-click')
      } else {
        mainPortal.classList.remove('no-click')
      }
    }
  }, [isSubmitting])

  useEffect(() => {
    if (applicationTypeBulkFilter) {
      if (applicationTypeBulkFilter === 'ALL') {
        setCheckedApplications(
          selectedTab === 'NEW' ?
            applicationsList.applications.map(app => app.id) :
          selectedTab === 'RENEWAL' ?
            renewalsList.renewals.map(app => app.id) :
            expiringPoliciesToShow.map(app => app.renewalId || app.applicationId)
        )
      } else {
        setCheckedApplications(
          selectedTab === 'NEW' ?
            applicationsList.applications
              .map(app => app.applicantType === applicationTypeBulkFilter ? app.id : 0)
              .filter(id => id > 0) :
            renewalsList.renewals
              .map(app => app.userType === applicationTypeBulkFilter ? app.id : 0)
              .filter(id => id > 0)
        )
      }
    } else {
      setCheckedApplications([])
    }
  }, [applicationTypeBulkFilter, applicationsList.applications, expiringPoliciesToShow, renewalsList.renewals, selectedTab])

  const handleFetchParams = useCallback(() => {
    let params: { [key: string]: number | string | boolean } = {
      PageSize: 10,
      PageNumber: currentPage
    }
    if (applicationStatusFilter !== 'EXPIRING_POLICIES') {
      params['Status'] = statusValuesToFetchApplications[applicationStatusFilter]
    }

    switch (sortingOption) {
      case 'Alphabetical A - Z':
        params['SortField'] = selectedTab === 'NEW' ? 'applicantName' : 'userName'
        params['SortBy'] = 0
        break;
      case 'Alphabetical Z - A':
        params['SortField'] = selectedTab === 'NEW' ? 'applicantName' : 'userName'
        params['SortBy'] = 1
        break;
      case 'Oldest to Newest':
        params['SortField'] = 'dateUpdated'
        params['SortBy'] = 0
        break;
      case 'Newest to Oldest':
        params['SortField'] = 'dateUpdated'
        params['SortBy'] = 1
        break;
      case 'Progress 0% - 100%':
        params['SortField'] = 'percentageOfCompletion'
        params['SortBy'] = 0
        break;
      case 'Progress 100% - 0%':
        params['SortField'] = 'percentageOfCompletion'
        params['SortBy'] = 1
        break;
    }

    const now = new Date()

    if (applicationStatusFilter !== 'EXPIRING_POLICIES') {
      switch (dateRangeOption) {
        case '30 days':
          params['DurationFromDate'] = subDays(now, 30).toISOString()
          break;
        case '60 days':
          params['DurationFromDate'] = subDays(now, 60).toISOString()
          break;
        case '90 days':
          params['DurationFromDate'] = subDays(now, 90).toISOString()
          break;
        case 'Last 12 months':
          params['DurationFromDate'] = subMonths(now, 12).toISOString()
          break;
        case 'Month to date':
          params['DurationFromDate'] = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
          break;
        case 'Year to date':
          params['DurationFromDate'] = new Date(now.getFullYear(), 0, 1).toISOString()
          break;
      }
    }

    if (selectedTab === 'RENEWAL' && applicationStatusFilter === 'EXPIRING_POLICIES') {
      params["ExpiryPolicies"] = true
    }

    return params
  }, [applicationStatusFilter, currentPage, dateRangeOption, selectedTab, sortingOption])

  const fetchApplications = useCallback(async () => {
    setIsLoadingApplications(true)
    setApplicationsList(prev => ({ ...prev, applications: [] }))
    setRenewalsList(prev => ({ ...prev, renewals: [] }))
    try {
      if (selectedTab === 'NEW') {
        const { data } = await api.get('/Applications/all', {
          params: handleFetchParams()
        })
        setApplicationsList(data)
      } else {
        const { data } = await api.get('/Renewals/all', {
          params: handleFetchParams()
        })
        setRenewalsList(data)
      }
    } catch (error) {
    } finally {
      setIsLoadingApplications(false)
    }
  }, [handleFetchParams, selectedTab])

  const fetchExpiringPolicies = useCallback(async () => {
    setIsLoadingApplications(true)
    try {
      const { data } = await api.get<ExpiringPoliciesListData[]>('Policies/GetExpiringPolicies', {
        params: {
          asOfDate: addDays(new Date(), 60).toISOString()
        }
      })
      const sorted = data.sort((a, b) =>
        (!!a.expiryDate ? new Date(a.expiryDate).getTime() : 0) - (!!b.expiryDate ? new Date(b.expiryDate).getTime() : 0)
      )
      setExpiringPoliciesList(sorted)
    } catch (_) { } finally {
      setIsLoadingApplications(false)
    }
  }, [])

  useEffect(() => {
    if (selectedTab !== 'EXPIRING_POLICIES') {
      fetchApplications()
    } else {
      fetchExpiringPolicies()
    }
  }, [fetchApplications, fetchExpiringPolicies, selectedTab])

  const handleStatusSelect = (status: AppStatusFilterOptions) => {
    setApplicationStatusFilter(status)
    setApplicationTypeBulkFilter(undefined)
    setCheckedApplications([])
    setCurrentPage(1)
  }

  const handleCloseDrawer = () => {
    setOpenApplicationId(0)
    setDrawerApplication(undefined)
    setDrawerHidden(false)
    setopenSelectContactForSubmit([])
  }

  const changePageOnSubmit = () => {
    if (applicationsList.applications.length > 1) return

    if (currentPage === 1) return

    setCurrentPage(prev => prev - 1)
  }

  const handleRowSubmitApplication = async (appId: number) => {
    setIsSubmitting(true)
    setDrawerHidden(true)
    setOpenApplicationId(appId)
  }

  const handleHeaderSubmitApplication = async () => {
    const thisApplication = applicationsList.applications.find(app => app.id === openApplicationId);
    const isJudge = thisApplication?.applicantType === "JUDGE";
    let data = []
    if (selectedTab === 'NEW') {
      try {
        const result = await api.get(`/ISI/firmcontacts/${openApplicationId}`)
        data = result.data
      } catch(e) {
        setIsSubmitting(false)
      }
    }

    if (data.length > 0) {
      setopenSelectContactForSubmit(data)
      setDrawerHidden(false)
    } else {
      try {
        if(selectedTab === 'NEW') {
          await api.post(`/Applications/submit`, {
            applicationId: openApplicationId,
            createNewFirmContact: true,
            existingFirmContactKey: '',
            existingClientKey: '',
          })
        }
        if(selectedTab === 'RENEWAL') {
          await api.post(`/Renewals/submit/${openApplicationId}`)
        }
        setIsSubmitting(false)
        changePageOnSubmit()
        fetchApplications()
        const applicantName = applicationsList.applications.find(app => app.id === openApplicationId)?.applicantName
        callToast({
          header: `${applicantName ?? ''} application successfully submitted`,
          footer: 'You can now view it in the submitted applications tab'
        })
      } catch (error:any) {
        setIsSubmitting(false)

        // Log actual error details to browser console
        const errorDetails = error?.response?.data?.errorDetails
        if(errorDetails) {
          console.error(errorDetails)
        }

        callToast({
          header: 'Error trying to submit application',
          footer: `${error?.response?.data?.errorMessage ?? 'Please try again later' }`,
          type: 'error'
        })
      } finally {
        setIsSubmitting(false)
        handleCloseDrawer()
      }
    }
  }

  const handleRowSubmitRenewal = async (appId: number) => {
    setIsSubmitting(true)
    setDrawerHidden(true)
    setOpenApplicationId(appId)
  }

  const handleExistingContactSubmitApplication =
    async (selection: ISIExistingContactData) => {

      try {
        const applicantName = applicationsList.applications.find(app => app.id === openApplicationId)?.applicantName
        if (selection == NewContactSelection) {
          await api.post(`/Applications/submit`, {
            applicationId: openApplicationId,
            createNewFirmContact: true,
            existingFirmContactKey: '',
            existingClientKey: ''
          })
        }
        else {
          await api.post(`/Applications/submit`, {
            applicationId: openApplicationId,
            createNewFirmContact: false,
            existingFirmContactKey: selection.contactKey,
            existingClientKey: selection.clientKey
          })
        }
        setIsSubmitting(false)
        handleCloseDrawer()
        changePageOnSubmit()
        fetchApplications()
        callToast({
          header: `${applicantName ?? ''} application successfully submitted`,
          footer: 'You can now view it in the submitted applications tab'
        })
      } catch (error : any) {
        setIsSubmitting(false)
        callToast({
          header: 'Error trying to submit application',
          footer: `${error?.response?.data?.errorMessage ?? 'Please try again later'}`,
          type: 'error'
        })
      }
    }

  const handleBulkContact = () => {
    let emails

    if (selectedTab === 'EXPIRING_POLICIES') {
      emails = expiringPoliciesToShow
        .filter(app => checkedApplications.includes((app.renewalId || app.applicationId)))
        .map(app => app.renewalApplicantEmail || app.applicantEmail)
    } else if (selectedTab === 'NEW') {
      emails = applicationsList.applications
        .filter(app => checkedApplications.includes(app.id))
        .map(app => app.applicantEmail)
    } else {
      emails = renewalsList.renewals
        .filter(app => checkedApplications.includes(app.id))
        .map(app => app.userEmail)
    }

    return `mailto:${emails.join(';')}`
  }

  const handleSendReminders = useCallback(async (appIds: number[]) => {
    if (appIds.length === 0) return;

    const isNewTab = selectedTab === 'NEW';
    const request = {
      endpoint: isNewTab ? '/Applications/Reminder' : '/Renewals/Reminder',
      body: isNewTab ? { applicationIds: appIds } : { renewalIds: appIds },
    };

    try {
      await api.post(request.endpoint, request.body);

      const sendToast = (header: string) => {
        callToast({
          header,
          footer: 'Reminders dates have been updated',
        });
      };

      if (appIds.length === 1) {
        let applicantName = '';

        if (isNewTab) {
          applicantName = applicationsList.applications.find(app => app.id === appIds[0])?.applicantName || '';
        } else {
          const renewal = await api.get(`Renewals/${appIds[0]}`).then(res => res.data);
          const contactData: FirmDetailsContactData = await api.get(`Firm/GetApplicantUserPseudoContact?firmId=${renewal.firmId}`).then(res => res.data);
          applicantName = `${contactData.firstName} ${contactData.lastName}`;
        }

        sendToast(`Reminder sent to ${applicantName}`);
      } else {
        sendToast(`Reminder sent to ${appIds.length} Applicants`);
      }

      fetchApplications();
    } catch (e) {
      callToast({
        header: 'Error trying to send reminder',
        footer: 'Please, try again later',
        type: 'error',
      });
    }
  }, [applicationsList.applications, callToast, fetchApplications, selectedTab]);

  const handleRejectApplications = async (appIds: number[]) => {
    if (appIds.length > 0) {
      try {
        if (selectedTab === 'NEW') {
          await api.post('/Applications/reject', {
            applicationIds: appIds
          })
        } else {
          await Promise.all(appIds.map(async id => api.patch(`/Renewals/reject/${id}`)))
        }
        callToast({
          header: `${appIds.length} application(s) successfully rejected`,
          footer: `You can now view in the rejected applications tab`,
        })
        handleCloseDrawer()
        changePageOnSubmit()
        fetchApplications()
      } catch (e) {
        callToast({
          header: 'Error trying to reject application',
          footer: 'Please, try again later',
          type: 'error'
        })
      }
    }
  }

  const handleSendApplicationMagicLink = async (appIds: number[]) => {
    if (appIds.length > 0) {
      try {
        await api.post('/MagicLinks/Impersonate', {
          applicationIds: appIds,
          applicationType: selectedTab === 'NEW' ? 1 : 2 //1 is application and 2 is renewal
        })

        callToast({
          header: 'Magic Link successfully sent',
          footer: 'You can check your mailbox for the selected application',
        })
        handleCloseDrawer()
        changePageOnSubmit()
        fetchApplications()
      } catch (e) {
        callToast({
          header: 'Error trying to send magic link',
          footer: 'Please, try again later',
          type: 'error'
        })
      }
    }
  }

  const handleDeleteApplications = async (appIds: number[]) => {
    if (appIds.length > 0) {
      try {
        if (selectedTab === 'NEW') {
          await api.delete('/Applications', {
            data: {
              applicationIds: appIds
            }
          })
        } else {
          await Promise.all(appIds.map(async id => api.delete(`/Renewals/${id}`)))
        }
        callToast({
          header: `${appIds.length} applications successfully deleted`,
        })
        handleCloseDrawer()
        changePageOnSubmit()
        fetchApplications()
      } catch (e) {
        callToast({
          header: 'Error trying to reject application',
          footer: 'Please, try again later',
          type: 'error'
        })
      }
    }
  }

  const handleSelectTab = (selection: NavTabOptions) => {
    setSelectedTab(selection)
    setApplicationsList(prev => ({ ...prev, applications: [] }))
    setRenewalsList(prev => ({ ...prev, renewals: [] }))
    setExpiringPoliciesList([])
    if (selection !== 'EXPIRING_POLICIES') {
      handleStatusSelect('COMPLETED')
    }
  }

  const isBulkSelectionAvailable = (applicationsList.applications && applicationsList.applications.length > 0) ||
    (renewalsList.renewals && renewalsList.renewals.length > 0)

  const handleRejectApps = async (appIds: number[]) => {
    setRejectModalBtnsDisabled(true)
    try {
      await handleRejectApplications(appIds)
      setRejectAppModalOpen(false)
    } catch (e) { } finally {
      setRejectModalBtnsDisabled(false)
    }
  }

  const handleImpersonateApps = async (appIds: number[]) => {
    setImpersonateModalBtnsDisabled(true)
    try {
      await handleSendApplicationMagicLink(appIds)
      setImpersonateModalOpen(false)
    } catch (e) { } finally {
      setImpersonateModalBtnsDisabled(false)
    }
  }

  const downloadApplication = () => {
    setIsLoadingScreenshot(true);

    var appSummaryOriginalCss = appSummaryRef.current.style.cssText;

    var appSummaryStyle = appSummaryRef.current.style;
    appSummaryStyle.position = "fixed";
    appSummaryStyle.top = "0";
    appSummaryStyle.left = "0";
    appSummaryStyle.right = "0";
    appSummaryStyle.padding = "0";
    appSummaryStyle.width = "unset !important";
    appSummaryStyle.cssText += ";" + "height: unset !important";

    takeScreenShot(appSummaryRef.current)
      .then((image: any, { name = "Application Summary", extension = "jpg" } = {}) => {
        const a = document.createElement("a");
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
      }).then(() => {
        appSummaryStyle.cssText = appSummaryOriginalCss;
        setIsLoadingScreenshot(false);
      });
  };

  return (
    <AdminPortalApplications>
      <header>
        <nav>
          <PortalNavTab
            selected={selectedTab === 'NEW'}
            onClick={() => handleSelectTab('NEW')}
          >
            New
          </PortalNavTab>
          <PortalNavTab
            selected={selectedTab === 'RENEWAL'}
            onClick={() => handleSelectTab('RENEWAL')}
          >
            Renewal
          </PortalNavTab>
          <PortalNavTab
            selected={selectedTab === 'EXPIRING_POLICIES'}
            onClick={() => handleSelectTab('EXPIRING_POLICIES')}
          >
            Expiring Policies
          </PortalNavTab>
        </nav>
        {selectedTab !== 'EXPIRING_POLICIES' ? (
          <section>
            <>
              <FlexDiv gap="16px" align='center'>
                <PillButton
                  selected={applicationStatusFilter === 'IN_PROGRESS'}
                  onClick={() => handleStatusSelect('IN_PROGRESS')}
                >
                  {statusToCapitalizedName.IN_PROGRESS}
                </PillButton>
                <PillButton
                  selected={applicationStatusFilter === 'COMPLETED'}
                  onClick={() => handleStatusSelect('COMPLETED')}
                >
                  {statusToCapitalizedName.COMPLETED}
                </PillButton>
                <PillButton
                  selected={applicationStatusFilter === 'SUBMITTED'}
                  onClick={() => handleStatusSelect('SUBMITTED')}
                >
                  {statusToCapitalizedName.SUBMITTED}
                </PillButton>
                <PillButton
                  selected={applicationStatusFilter === 'REJECTED'}
                  onClick={() => handleStatusSelect('REJECTED')}
                >
                  {statusToCapitalizedName.REJECTED}
                </PillButton>
              </FlexDiv>
            </>
            <FlexDiv margin="0 0 0 auto" gap="22px" maxWidth='fit-content'>
              <StandardSelect
                width="fit-content"
                value={sortingOption}
                onChange={e => setSortingOption(e.target.value as SortingOptions)}
              >
                {dropdownFilterOptions.map((item, index) => (
                  <option key={index} value={item}>{item}</option>
                ))}
              </StandardSelect>
              <StandardSelect
                width="fit-content"
                value={dateRangeOption}
                onChange={e => setDateRangeOption(e.target.value as DateRangeOptions)}
              >
                {dropdownDatesFilterOptions.map((item, index) => (
                  <option key={index} value={item}>{item}</option>
                ))}
              </StandardSelect>
            </FlexDiv>
          </section>
        ) : <section />}
        {isBulkSelectionAvailable ? (
          <BulkSelectOptions>
            <BulkSelect
              selection={applicationTypeBulkFilter}
              onSelect={selected => setApplicationTypeBulkFilter(selected)}
              applicationStatus={selectedTab === 'EXPIRING_POLICIES' ? 'REJECTED' : applicationStatusFilter}
            />
            {(applicationTypeBulkFilter || checkedApplications.length > 0) ? (
              <FlexDiv gap="24px">
                {selectedTab === 'EXPIRING_POLICIES' ? (
                  <>
                    <a href={handleBulkContact()} style={{ textDecoration: 'none' }}>
                      <CircleActionButton
                        label="Contact"
                        onClick={e => e.stopPropagation()}
                      >
                        <TbMail />
                      </CircleActionButton>
                    </a>
                  </>
                ) : (
                  <>
                    {applicationStatusFilter === 'IN_PROGRESS' ? (
                      <CircleActionButton
                        label="Send Reminder"
                        onClick={() => handleSendReminders(checkedApplications)}
                      >
                        <FiSend />
                      </CircleActionButton>
                    ) : null}
                    {applicationStatusFilter !== 'IN_PROGRESS' ? (
                      <>
                        <CircleActionButton
                          label="Download"
                        >
                          <MdOutlineDownload />
                        </CircleActionButton>
                        <a href={handleBulkContact()} style={{ textDecoration: 'none' }}>
                          <CircleActionButton
                            label="Contact"
                            onClick={e => e.stopPropagation()}
                          >
                            <TbMail />
                          </CircleActionButton>
                        </a>
                      </>
                    ) : null}
                    {['IN_PROGRESS', 'COMPLETED'].includes(applicationStatusFilter) ? (
                      <CircleActionButton
                        label="Reject"
                        onClick={() => setRejectAppModalOpen(true)}
                      >
                        <MdClose />
                      </CircleActionButton>
                    ) : null}
                    {['IN_PROGRESS'].includes(applicationStatusFilter) ? (
                      <CircleActionButton
                        label="Impersonate"
                        onClick={() => setImpersonateModalOpen(true)}
                      >
                        <MdRemoveRedEye />
                      </CircleActionButton>
                    ) : null}
                    {applicationStatusFilter === 'REJECTED' ? (
                      <CircleActionButton
                        label="Delete"
                        onClick={() => handleDeleteApplications(checkedApplications)}
                      >
                        <MdDeleteOutline />
                      </CircleActionButton>
                    ) : null}
                  </>
                )}
              </FlexDiv>
            ) : null}
          </BulkSelectOptions>
        ) : null}
      </header>
      {selectedTab === 'NEW' ? (
        <main>
          {(applicationsList.applications && applicationsList.applications.length > 0) ?
            applicationsList.applications.map(application => (
              <AppListRow
                key={application.id}
                rowType={applicationStatusFilter}
                checked={checkedApplications.includes(application.id)}
                onClick={id => setCheckedApplications(prev => prev.includes(id) ? prev.filter(app => app !== id) : [...prev, id])}
                rowData={application}
                openAppDrawer={() => setOpenApplicationId(application.id)}
                submitApplication={handleRowSubmitApplication}
                sendReminder={handleSendReminders}
                rejectApplication={handleRejectApplications}
                sendMagicLink={handleSendApplicationMagicLink}
                deleteApplication={handleDeleteApplications}
              />
            )) : isLoadingApplications ? (
              <FlexDiv maxWidth='100%' height='100%' align='center' justify='center'>
                <img src={loadingImg} alt="loading..." />
              </FlexDiv>
            ) : (
              <TextMessage>There are currently no {statusToCapitalizedName[applicationStatusFilter]} applications. </TextMessage>
            )}
        </main>
      ) : selectedTab === 'RENEWAL' ? (
        <main>
          {(renewalsList.renewals && renewalsList.renewals.length > 0) ?
            renewalsList.renewals.map(application => (
              <RenewalListRow
                key={application.id}
                rowType={applicationStatusFilter}
                checked={checkedApplications.includes(application.id)}
                onClick={id => setCheckedApplications(prev => prev.includes(id) ? prev.filter(app => app !== id) : [...prev, id])}
                rowData={application}
                openAppDrawer={() => setOpenApplicationId(application.id)}
                submitApplication={handleRowSubmitRenewal}
                sendReminder={handleSendReminders}
                rejectApplication={handleRejectApplications}
                sendMagicLink={handleSendApplicationMagicLink}
                deleteApplication={handleDeleteApplications}
              />
            )) : isLoadingApplications ? (
              <FlexDiv maxWidth='100%' height='100%' align='center' justify='center'>
                <img src={loadingImg} alt="loading..." />
              </FlexDiv>
            ) : (
              <TextMessage>There are currently no {statusToCapitalizedName[applicationStatusFilter]} applications. </TextMessage>
            )}
        </main>
      ) : (
        <main>
          {(expiringPoliciesList.length > 0) ?
            expiringPoliciesToShow.map((application) => (
              <ExpiringPoliciesListRow
                key={application.policyKey}
                checked={checkedApplications.includes(application.renewalId || (application.applicationId))}
                onClick={id => setCheckedApplications(prev => prev.includes(id) ? prev.filter(app => app !== id) : [...prev, id])}
                rowData={application}
                openAppDrawer={() => {
                  const isNewBusiness = !application.renewalId
                  setOpenApplicationId(application.renewalId || (application.applicationId))
                  setDrawerExpiringAplicationType(isNewBusiness ? 'NEW' : 'RENEWAL')
                }}
              />
            )) : isLoadingApplications ? (
              <FlexDiv maxWidth='100%' height='100%' align='center' justify='center'>
                <img src={loadingImg} alt="loading..." />
              </FlexDiv>
            ) : (
              <TextMessage>There are currently no Expiring Policies applications. </TextMessage>
            )}
        </main>
      )}
      {selectedTab === 'NEW' && applicationsList.totalPages > 1 ? (
        <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={applicationsList.totalPages} />
      ) : null}
      {selectedTab === 'RENEWAL' && renewalsList.totalPages > 1 ? (
        <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={renewalsList.totalPages} />
      ) : null}
      {selectedTab === 'EXPIRING_POLICIES' && Math.ceil(expiringPoliciesList.length / 5) > 1 ? (
        <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={Math.ceil(expiringPoliciesList.length / 5)} />
      ) : null}
      <PortalRightDrawer
        open={!!openApplicationId}
        toggle={handleCloseDrawer}
        hidden={drawerHidden}
        header={openSelectContactForSubmit.length === 0 ? (
          <DrawerHeader
            applicationStatus={selectedTab === 'EXPIRING_POLICIES' ? 'EXPIRING_POLICIES' : applicationStatusFilter}
            applicationData={(() => {
              if(selectedTab === 'EXPIRING_POLICIES' && drawerExpiringAplicationType === 'NEW') {
                const app = expiringPoliciesList.find(exp => exp.applicationId === openApplicationId)
                return {
                  applicantEmail: app?.applicantEmail,
                  applicantName: app?.applicantName,
                  dateStarted: app?.effectiveDate,
                  dateRejected: app?.expiryDate
                }
              }
              return applicationsList.applications.find(app => app.id === openApplicationId)
            })()}
            renewalData={(() => {
              if(selectedTab === 'EXPIRING_POLICIES' && drawerExpiringAplicationType !== 'NEW') {
                const app = expiringPoliciesList.find(exp => exp.renewalId === openApplicationId)
                return {
                  userEmail: app?.renewalApplicantEmail,
                  userName: app?.renewalApplicantName,
                  dateStarted: app?.effectiveDate,
                  dateRejected: app?.expiryDate
                }
              }
              return renewalsList.renewals.find(app => app.id === openApplicationId)
            })()}
            submitApplication={() => handleHeaderSubmitApplication()}
            sendReminder={handleSendReminders}
            rejectApplication={handleRejectApplications}
            sendMagicLink={handleSendApplicationMagicLink}
            deleteApplication={handleDeleteApplications}
            isNewBusiness={selectedTab === 'EXPIRING_POLICIES' ? drawerExpiringAplicationType === 'NEW' : selectedTab === 'NEW'}
            submitHidden={drawerHidden}
            applicationLoaded={!!drawerApplication}
            downloadApplication={downloadApplication}
          />
        ) : null
        }
      >
        {openSelectContactForSubmit.length > 0 ? (
          <ExistingContactSelect
            removeNoClick = {true}
            onCancel={() => setopenSelectContactForSubmit([])}
            selectionList={openSelectContactForSubmit}
            submitApplication={handleExistingContactSubmitApplication}
          />
        ) : (
          <AppListDrawerContent
            applicationId={openApplicationId}
            isNewBusiness={selectedTab === 'EXPIRING_POLICIES' ? drawerExpiringAplicationType === 'NEW' : selectedTab === 'NEW'}
            application={drawerApplication}
            isJudge={drawerApplication?.insuranceType === 'JUDGE'}
            setApplication={setDrawerApplication}
            primaryContactData = {primaryContactData}
            setPrimaryContactData = {setPrimaryContactData}
            appSummaryRef={appSummaryRef}
          />
        )}
      </PortalRightDrawer>
      <Modal
        isOpen={rejectAppModalOpen}
        onRequestClose={() => setRejectAppModalOpen(false)}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to reject the selected {selectedTab === 'NEW' ? 'application' : 'renewal'}(s)?</strong>
          <span>Once an {selectedTab === 'NEW' ? 'application' : 'renewal'} is rejected, you will not be able to submit it to ISI.</span>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={() => handleRejectApps(checkedApplications)}
              disabled={rejectModalBtnsDisabled}
            >
              Reject {selectedTab === 'NEW' ? 'application' : 'renewal'}(s)
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={() => setRejectAppModalOpen(false)}
              disabled={rejectModalBtnsDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>
 {/* magicklink */}
      <Modal
        isOpen={impersonateModalOpen}
        onRequestClose={() => setImpersonateModalOpen(false)}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to impersonate the user of the selected {selectedTab === 'NEW' ? 'application' : 'renewal'}(s)?</strong>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={() => handleImpersonateApps(checkedApplications)}
              disabled={impersonateModalBtnsDisabled}
            >
              Impersonate
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={() => setImpersonateModalOpen(false)}
              disabled={impersonateModalBtnsDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>

      {isLoadingScreenshot && (
        <ScreenshotLoadingContainer>
          <img src={loadingImg} alt="loading" style={{margin: "49vh 49vw"}} />
        </ScreenshotLoadingContainer>
      )}
    </AdminPortalApplications>
  )
}

export default Applications